import { 
  Admin, 
  Resource,
  CustomRoutes, 
  localStorageStore,
  useStore,
  StoreContextProvider
} from 'react-admin';

import polyglotI18nProvider from 'ra-i18n-polyglot';
import englishMessages from './i18n/en';
import hindiMessages from './i18n/en';
import { Route, Navigate } from 'react-router-dom';
import jsonServerProvider from 'ra-data-json-server';
import httpClient from './utils/httpClient';
import Segments from './segments/Segments';
import authProvider from './auth/authProvider';
//import { themes, ThemeName } from './themes/themes';
import { Layout, Login } from './layout';
import categories from './categories';
import subCategories from './subCategories';
import productSegments from './productSegments';
import products from './products';
import productRecycle from './productRecycle';
import users from './users';
import customers from './customers';
import productPricing from './productPricing';
import SellerFilterList from './sellerFilter/SellerFilterList';
import ProductShippingList from './productShipping/ProductShippingList';
import ProductHistory  from './productHistory/productHistory';
import OrderHistory from './orderHistory'
import ProductFavorite from './productFavorite/productFavorite';
import states from './states';
import stateCities from './stateCities';
import cityShipping from './cityShipping';
import orders from './orders';
import { Dashboard } from './dashboard';
import NotAuthorized from './notAuthorized';
import unregistered from "./unregistered"
import offers from './offers';
import blogs from './blogs';
import bids from './bids';
import inquiries from './inquiries';
import directoryCategories from './directoryCategories';
import directorySubcategories from './directorySubcategories';
import directoryContacts from './directoryContacts';
import customerContacts from './customerContacts';
import SettingsPage from './settings/SettingsPage';
import CacheSettings from './settings/CacheSettings';
import CustomerFavorite from './customerFavorite/customerFavorite';
import DashboardCustomers from './customers/DashboardCustomers';
import DashboardLinks from './links/DashboardLinks';
import DashboardActivities from './activities/DashboardActivities';
import DailyPricing from './dailyPricing/DailyPricing';
import DashboardSessions from './customers/DashboardSessions';
import DashboardFunnels from './customers/DashboardFunnels';
import DashboardSeo from './seo/DashboardSeo';
import DailyAlerts from './products/DailyAlerts';
import sellerRegistrationRequests from './sellerRegistrationRequests';
import { withResolvedBlogImage } from './utils/resolveUploadUrl';
import ImportDashboard from './importExport/ImportDashboard';
import ImportUpload from './importExport/ImportUpload';
import ImportBatchDetail from './importExport/ImportBatchDetail';
import ImportMasters from './importExport/ImportMasters';
import ImportTrendDashboard from './importExport/ImportTrendDashboard';


const i18nProvider = polyglotI18nProvider(
  locale => {
      if (locale === 'hi') {
        return import('./i18n/hi').then(messages => messages.default);
    }
      // Always fallback on english
      return englishMessages;
  },
  'en',
  [
      { locale: 'en', name: 'English' },
      { locale: 'hi', name: 'हिंदी' },
  ]
);

const store = localStorageStore(process.env.CURRENT_VERSION, process.env.APP_MODULE);


const App = () => {
    //const [themeName] = useStore<ThemeName>('themeName', 'soft');
    //const lightTheme = themes.find(theme => theme.name === themeName)?.light;
    //const darkTheme = themes.find(theme => theme.name === themeName)?.dark;
    // Create dataProvider with custom httpClient that adds Authorization header
    const dataProvider = jsonServerProvider(process.env.REACT_APP_API_URL, httpClient);

    const apiBase = process.env.REACT_APP_API_URL;

    const customDataProvider = {
        ...dataProvider,
        getList: (resource, params) => {
            if (resource === "blogs") {
                const { page, perPage } = params.pagination;
                const { field, order } = params.sort;
                const f = params.filter || {};
                const query = new URLSearchParams();
                query.set('_start', String((page - 1) * perPage));
                query.set('_end', String(page * perPage));
                query.set('_sort', field || 'created_at');
                query.set('_order', order || 'DESC');
                if (f.title) query.set('title', f.title);
                if (f.status) query.set('status', f.status);
                return httpClient(`${apiBase}/blogs?${query}`).then(({ json, headers }) => ({
                    data: Array.isArray(json) ? json.map(withResolvedBlogImage) : json,
                    total: parseInt(headers.get('x-total-count') || '0', 10),
                }));
            }
            return dataProvider.getList(resource, params);
        },
        getOne: (resource, params) => {
            if (resource === "blogs") {
                return dataProvider.getOne(resource, params).then(({ data }) => ({
                    data: withResolvedBlogImage(data),
                }));
            }
            return dataProvider.getOne(resource, params);
        },
    };
    
    // const dataProvider = jsonServerProvider(process.env.REACT_APP_API_URL); // Use your API's base URL
    return (
      <Admin 
        store={store}
        dataProvider={customDataProvider}
        layout={Layout}
        i18nProvider={i18nProvider}
        authProvider={authProvider}
        loginPage={Login}
        dashboard={Dashboard}
        //lightTheme={lightTheme}
        //darkTheme={darkTheme}
        //defaultTheme="light"
      >
        <CustomRoutes>
          {/* Resource routes use `name` only; `options.path` is ignored. Real path is /blogs. */}
          <Route path="/news" element={<Navigate to="/blogs" replace />} />
          <Route path="/segments" element={<Segments />} />
          <Route path="/not-authorized" element={<NotAuthorized />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/cache" element={<CacheSettings />} />
          <Route path="/dashboard/customers" element={<DashboardCustomers />} />
          <Route path="/dashboard/sessions" element={<DashboardSessions />} />
          <Route path="/dashboard/funnels" element={<DashboardFunnels />} />
          <Route path="/dashboard/links" element={<DashboardLinks />} />
          <Route path="/dashboard/activities" element={<DashboardActivities />} />
          <Route path="/dashboard/seo" element={<DashboardSeo />} />
          <Route path="/dailypricing" element={<DailyPricing />} />
          <Route path="/products/daily-alerts" element={<DailyAlerts />} />
          <Route path="/import" element={<ImportDashboard />} />
          <Route path="/import/trends" element={<ImportTrendDashboard />} />
          <Route path="/import/upload" element={<ImportUpload />} />
          <Route path="/import/batches/:id" element={<ImportBatchDetail />} />
          <Route path="/import/masters" element={<ImportMasters />} />
        </CustomRoutes>
        <Resource name="categories" {...categories} />
        <Resource name="subcategories" {...subCategories} />
        <Resource name="productsegments" {...productSegments} />
        <Resource name="products" {...products} />
        <Resource name="productrecycle" {...productRecycle} />
        <Resource name="productpricing" {...productPricing} />
        <Resource name="users" {...users} />
        <Resource name="customers" {...customers} />
        <Resource name="sellerFilter"  list={SellerFilterList} />
        <Resource name="productHistory" list={ProductHistory} />
        <Resource name="productfavorite" list={ProductFavorite} />
        <Resource name="states" {...states} />
        <Resource name="statecities" {...stateCities} />
        <Resource name="cityshipping" {...cityShipping} />
        <Resource name="productShipping"  list={ProductShippingList} />
        <Resource name="orders" {...orders} />
        <Resource name="orderhistory" {...OrderHistory} />
        <Resource name="unregistered" {...unregistered} />
        <Resource name="offers" {...offers} />
        <Resource name="blogs" {...blogs} />
        <Resource name="bids" {...bids} />
        <Resource name="inquiries" {...inquiries} />
        <Resource name="customerfavorite" list={CustomerFavorite} />
        <Resource name="directorycategories" {...directoryCategories} />
        <Resource name="directorysubcategories" {...directorySubcategories} />
        <Resource name="directorycontacts" {...directoryContacts} />
        <Resource name="customercontacts" {...customerContacts} />
        <Resource name="sellerRegistrationRequests" {...sellerRegistrationRequests} />

     </Admin>
    );
};

const AppWrapper = () => (
    <StoreContextProvider value={store}>
        <App />
    </StoreContextProvider>
);

export default AppWrapper;
